import { Footer } from "@/components/Footer";
import { NewsList } from "@/components/News/NewsList";
import { Nav } from "@/components/common/FrontendNav";
import { Option, Select } from "@/components/common/Select";
import { TeamsList } from "@/components/teams/TeamsList";
import { getBundesligaNews } from "@/data/news";
import { getActiveSeasons } from "@/data/season";
import { getBundesligaTeams, getLeagueTeamWithPlayers } from "@/helper/api";
import prisma from "@/helper/client";
import { LeagueTeamWithPlayers } from "@/models/LeagueTeam";
import { Season } from "@/models/Season";
import { News } from "@/models/types";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Head from "next/head";
import { useRouter } from "next/router";
import { setGhostCacheHeader } from "@/helper/cacheHeaders";

export async function getServerSideProps(context: any) {
  const { res } = context;
  setGhostCacheHeader(res);
  const regex = /^\d+-\d+$/;
  if (!regex.test(context.query.season)) {
    return {
      redirect: {
        destination: "/bundesliga/teams",
        permanent: false,
      },
    };
  }
  const [start, end] = context.query.season.split("-");
  const season = await prisma.season.findFirst({
    where: { startYear: parseInt(start), endYear: parseInt(end) },
  });

  if (!season) {
    return {
      redirect: {
        destination: "/bundesliga/teams",
        permanent: false,
      },
    };
  }

  const teams = await getBundesligaTeams(season.id);

  const fullTeams = await Promise.all(
    teams.map((team) =>
      getLeagueTeamWithPlayers(team, {
        orderBy: [
          { seasonPlayer: { qttrValue: { sort: "desc", nulls: "last" } } },
          { seasonPlayer: { lastName: "asc" } },
        ],
      }),
    ),
  );

  const { news } = await getBundesligaNews({
    locale: context.locale,
  });

  const seasons = await getActiveSeasons();

  return {
    props: {
      teams: fullTeams,
      news,
      seasons,
      selectedSeason: season,
      ...(await serverSideTranslations(context.locale)),
    },
  };
}

interface BundesligaTeamPageProps {
  teams: LeagueTeamWithPlayers[];
  news: News[];
  seasons: Season[];
  selectedSeason: Season;
}
export default function BundesligaTeamPage({
  teams,
  news,
  seasons,
  selectedSeason,
}: BundesligaTeamPageProps) {
  const { t } = useTranslation();
  const { push } = useRouter();

  const selectedSeasonQuery = `${selectedSeason.startYear}-${selectedSeason.endYear}`;

  return (
    <>
      <Head>
        <title>{`${t("Table_Tennis")} Bundesliga - TTBL - ${t(
          "Title_Teams",
        )}`}</title>
      </Head>
      <div>
        <Nav>
          <Select
            value={selectedSeasonQuery}
            onChange={(e) => push(`/bundesliga/teams/${e}`)}
            className="white lg:border-[1px] lg:ml-4"
          >
            {seasons.map((s) => (
              <Option
                key={s.id}
                value={`${s.startYear}-${s.endYear}`}
              >{`${s.startYear}-${s.endYear}`}</Option>
            ))}
          </Select>
        </Nav>
        <div className="mx-4 mb-4 lg:hidden">
          <Select
            value={selectedSeasonQuery}
            onChange={(e) => push(`/bundesliga/teams/${e}`)}
            className="white lg:border-[1px] lg:ml-4"
          >
            {seasons.map((s) => (
              <Option
                key={s.id}
                value={`${s.startYear}-${s.endYear}`}
              >{`${s.startYear}-${s.endYear}`}</Option>
            ))}
          </Select>
        </div>
        <TeamsList
          teams={teams}
          baseUrl={`/bundesliga/teams/${selectedSeasonQuery}`}
        />
        {news.length > 0 && (
          <div className="container container_py">
            <NewsList news={news} title="Bundesliga News" type="bundesliga" />
          </div>
        )}
        <Footer />
      </div>
    </>
  );
}
